import { useNavigate } from "react-router-dom";
import { useFetch, fmtDate, timeAgo } from "@/lib/shieldx";
import { PageHeader, Panel, Loader, Stat, EmptyState } from "@/components/shieldx/Primitives";
import { RiskBadge, StatusBadge } from "@/components/shieldx/Badges";
import {
  Activity, ShieldAlert, Siren, FileLock2, Eye, ShieldUser, Users, Gauge, ScrollText,
  Crosshair, HeartPulse, KeyRound, Search as SearchIcon,
} from "lucide-react";

const ROLE_META = {
  soc_lead: { title: "SOC Lead — Security Operations", icon: Activity, sub: "SOC-wide posture, active incidents, team workload and response performance." },
  security_analyst: { title: "Security Analyst — Investigation", icon: SearchIcon, sub: "Event investigation, threat signals, authorization decisions and policy violations." },
  incident_responder: { title: "Incident Responder — Active Response", icon: Siren, sub: "Assigned incidents, containment, response actions and recovery." },
  auditor: { title: "Auditor — Evidence & Traceability", icon: FileLock2, sub: "Read-only audit events, administrative actions, reports and evidence hashes." },
  viewer: { title: "Security Overview", icon: Eye, sub: "Authorized read-only view of security posture, assets, incidents and reports." },
  individual: { title: "Creator Guard", icon: ShieldUser, sub: "Account security, protected digital assets, authorization and alerts." },
  creator: { title: "Creator Guard", icon: ShieldUser, sub: "Account security, protected digital assets, authorization and alerts." },
};

function MiniBars({ dist, colors }) {
  const max = Math.max(1, ...Object.values(dist || {}));
  const entries = Object.entries(dist || {});
  if (!entries.length || entries.every(([, v]) => !v)) return <EmptyState title="INSUFFICIENT DATA" desc="No measured values for this period." icon={Gauge} />;
  return (
    <div className="space-y-2.5">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-3">
          <span className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-wide text-slate-400">{k.replace(/_/g, " ")}</span>
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: (colors && colors[k]) || "#2563eb" }} />
          </div>
          <span className="w-8 shrink-0 text-right font-mono tabular-nums text-sm text-slate-200">{v}</span>
        </div>
      ))}
    </div>
  );
}

function IncidentList({ rows, onOpen, empty = "No active incidents." }) {
  if (!rows?.length) return <EmptyState title={empty} desc="Nothing requires attention right now." icon={Siren} />;
  return (
    <div className="divide-y divide-slate-800/70">
      {rows.map((i) => (
        <button key={i.id} data-testid={`dash-incident-${i.id}`} onClick={() => onOpen(i)}
          className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-slate-800/30">
          <div className="min-w-0">
            <p className="truncate text-sm text-slate-200">{i.title}</p>
            <p className="truncate font-mono text-[11px] text-slate-500">{i.incident_id} · {i.target || "—"} · {i.created_at ? timeAgo(i.created_at) : ""}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <RiskBadge value={i.risk} /><StatusBadge value={i.state} />
          </div>
        </button>
      ))}
    </div>
  );
}

const RISK_COLORS = { LOW: "#10b981", MEDIUM: "#f59e0b", HIGH: "#f97316", CRITICAL: "#ef4444" };
const DEC_COLORS = { ALLOW: "#10b981", DENY: "#ef4444", BLOCK: "#f97316", REVIEW: "#f59e0b", CHALLENGE: "#06b6d4", ISOLATE: "#8b5cf6", TEMPORARY: "#60a5fa" };

export default function RoleDashboard() {
  const { data, loading } = useFetch("/dashboard");
  const navigate = useNavigate();
  if (loading || !data) return <Loader label="Resolving your dashboard" />;
  const meta = ROLE_META[data.role] || ROLE_META.viewer;
  const openInc = (i) => navigate(`/app/incidents/${i.id}`);

  return (
    <div>
      <PageHeader icon={meta.icon} title={meta.title} subtitle={meta.sub} />

      {data.role === "soc_lead" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Active Incidents" value={data.incidents_active} tone="amber" />
            <Stat label="Critical" value={data.critical} tone="red" />
            <Stat label="Team Members" value={data.team_size} tone="blue" />
            <Stat label="Simulations" value={data.simulations} tone="purple" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Severity Distribution"><MiniBars dist={data.by_severity} colors={RISK_COLORS} /></Panel>
            <Panel title="7-Day Incident Trend"><MiniBars dist={Object.fromEntries((data.incident_trend || []).map((p) => [p.label, p.value]))} /></Panel>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Critical Escalations"><IncidentList rows={data.escalations} onOpen={openInc} empty="No escalations." /></Panel>
            <Panel title="Analyst Workload"><MiniBars dist={data.analyst_workload} /></Panel>
          </div>
          <Panel title="Active Incident Queue"><IncidentList rows={data.recent_incidents} onOpen={openInc} /></Panel>
        </div>
      )}

      {data.role === "security_analyst" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Investigation Queue" value={data.investigation_queue?.length || 0} tone="cyan" />
            <Stat label="Threat Signals" value={data.threats_total} tone="amber" />
            <Stat label="Active Policies" value={data.policy_count} tone="blue" />
            <Stat label="Critical" value={data.critical} tone="red" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Authorization Decisions"><MiniBars dist={data.decisions} colors={DEC_COLORS} /></Panel>
            <Panel title="Recent Decisions">
              {data.recent_decisions?.length ? (
                <div className="divide-y divide-slate-800/70">
                  {data.recent_decisions.map((d, n) => (
                    <div key={n} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0"><p className="truncate text-sm text-slate-200">{d.subject || "—"}</p><p className="truncate font-mono text-[11px] text-slate-500">{d.reason || ""}</p></div>
                      <StatusBadge value={d.decision} />
                    </div>
                  ))}
                </div>
              ) : <EmptyState title="NO DATA AVAILABLE" icon={KeyRound} />}
            </Panel>
          </div>
          <Panel title="Investigation Queue" desc="Events awaiting analyst review"><IncidentList rows={data.investigation_queue} onOpen={openInc} empty="Queue is clear." /></Panel>
        </div>
      )}

      {data.role === "incident_responder" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Assigned Incidents" value={data.assigned_incidents?.length || 0} tone="amber" />
            <Stat label="Critical" value={data.critical} tone="red" />
            <Stat label="Recovery Points" value={data.recovery_points?.length || 0} tone="emerald" />
            <Stat label="Protected Assets" value={`${data.assets_protected}/${data.assets_total}`} tone="blue" />
          </div>
          <Panel title="My Assigned Incidents" desc="Detect → Validate → Contain → Isolate → Recover"><IncidentList rows={data.assigned_incidents} onOpen={openInc} empty="No incidents assigned." /></Panel>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Recovery Points" actions={<button onClick={() => navigate("/app/self-healing")} className="text-xs text-blue-400">Open</button>}>
              {data.recovery_points?.length ? data.recovery_points.map((r, n) => (
                <div key={n} className="flex items-center justify-between border-b border-slate-800/60 py-2"><span className="text-sm text-slate-200">{r.name}</span><StatusBadge value={r.status} /></div>
              )) : <EmptyState title="NO DATA AVAILABLE" icon={HeartPulse} />}
            </Panel>
            <Panel title="Recent Response Actions">
              {data.response_actions?.length ? data.response_actions.map((a, n) => (
                <div key={n} className="flex items-center justify-between border-b border-slate-800/60 py-2"><div className="min-w-0"><p className="truncate text-sm text-slate-200">{a.action}</p><p className="truncate font-mono text-[11px] text-slate-500">{a.target}</p></div><span className="font-mono text-[11px] text-slate-500">{a.ts ? timeAgo(a.ts) : ""}</span></div>
              )) : <EmptyState title="NO DATA AVAILABLE" icon={Activity} />}
            </Panel>
          </div>
        </div>
      )}

      {data.role === "auditor" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Audit Events" value={data.audit_events?.length || 0} tone="blue" />
            <Stat label="Policies" value={data.policy_count} tone="cyan" />
            <Stat label="Simulations" value={data.simulations} tone="purple" />
            <Stat label="Reports" value={data.reports} tone="emerald" />
          </div>
          <Panel title="Audit Trail" desc="Read-only · tamper-evident" actions={<button onClick={() => navigate("/app/audit-vault")} className="text-xs text-blue-400">Audit Vault</button>}>
            {data.audit_events?.length ? (
              <div className="divide-y divide-slate-800/70">
                {data.audit_events.map((a, n) => (
                  <div key={n} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0"><p className="truncate text-sm text-slate-200">{a.action} · <span className="text-slate-400">{a.target}</span></p><p className="truncate font-mono text-[11px] text-slate-500">{a.actor} · {a.correlation_id}</p></div>
                    <span className={`font-mono text-xs ${a.result === "DENIED" ? "text-rose-400" : "text-emerald-400"}`}>{a.result}</span>
                  </div>
                ))}
              </div>
            ) : <EmptyState title="NO DATA AVAILABLE" icon={FileLock2} />}
          </Panel>
        </div>
      )}

      {data.role === "viewer" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Protected Assets" value={`${data.assets_protected}/${data.assets_total}`} tone="blue" />
            <Stat label="Active Incidents" value={data.incidents_active} tone="amber" />
            <Stat label="Critical" value={data.critical} tone="red" />
            <Stat label="Reports" value={data.reports} tone="emerald" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Incidents by Risk"><MiniBars dist={data.by_severity} colors={RISK_COLORS} /></Panel>
            <Panel title="Assets">
              {data.assets?.length ? data.assets.map((a, n) => (
                <div key={n} className="flex items-center justify-between border-b border-slate-800/60 py-2"><span className="truncate text-sm text-slate-200">{a.name}</span><StatusBadge value={a.state} /></div>
              )) : <EmptyState title="NO DATA AVAILABLE" icon={Eye} />}
            </Panel>
          </div>
          <Panel title="Recent Incidents (read-only)"><IncidentList rows={data.recent_incidents} onOpen={openInc} /></Panel>
        </div>
      )}

      {(data.role === "individual" || data.role === "creator") && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Protected Assets" value={data.protected_assets?.length || 0} tone="blue" />
            <Stat label="Active Incidents" value={data.incidents_active} tone="amber" />
            <Stat label="Security Score" value={data.security_score ?? "—"} tone="emerald" />
            <Stat label="Platforms" value={data.connected_platforms?.length || 0} tone="purple" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Protected Digital Assets" actions={<button onClick={() => navigate("/app/protection")} className="text-xs text-blue-400">Protection</button>}>
              {data.protected_assets?.length ? data.protected_assets.map((a, n) => (
                <div key={n} className="flex items-center justify-between border-b border-slate-800/60 py-2"><div className="min-w-0"><p className="truncate text-sm text-slate-200">{a.name}</p><p className="truncate font-mono text-[11px] text-slate-500">{a.url || a.type}</p></div><StatusBadge value={a.state} /></div>
              )) : <EmptyState title="NO DATA AVAILABLE" desc="Register a website or account to protect it." icon={ShieldUser} />}
            </Panel>
            <Panel title="Connected Platforms">
              {data.connected_platforms?.length ? data.connected_platforms.map((p, n) => (
                <div key={n} className="flex items-center justify-between border-b border-slate-800/60 py-2"><span className="text-sm text-slate-200">{p.platform} · <span className="font-mono text-slate-400">{p.handle}</span></span><StatusBadge value={p.status} /></div>
              )) : <EmptyState title="NO PLATFORMS CONNECTED" icon={ShieldUser} />}
            </Panel>
          </div>
          <Panel title="Security Alerts & Incidents"><IncidentList rows={data.recent_incidents} onOpen={openInc} empty="No alerts." /></Panel>
        </div>
      )}
    </div>
  );
}
